/**
 * SecureFileBanner — SDE Phase 2 (s88).
 *
 * Persistent amber banner shown whenever the in-memory Cat A store holds values that
 * haven't been written to the .oesif Secure File yet. "Save secure file" runs the save
 * flow (silent write to the existing handle on Chrome/Edge; native picker / download
 * otherwise) and marks the values clean, which dismisses the banner. Re-appears when new
 * Cat A values are entered after saving.
 *
 * Must be mounted inside SensitiveDataSessionProvider.
 */
import { useState } from "react";
import { ShieldAlert, Loader2 } from "lucide-react";
import { useSensitiveDataSession } from "@/lib/sensitive-data-session-provider";
import { useToast } from "@/hooks/use-toast";
import { saveSessionSecureFile } from "@/lib/save-session-secure-file";

export function SecureFileBanner() {
  const session = useSensitiveDataSession();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  if (!session.hasUnsavedChanges) return null;
  const count = session.unsavedCount;

  const handleSave = async () => {
    setBusy(true);
    try {
      await saveSessionSecureFile(session);
      toast({ title: "Secure file saved", duration: 3000 });
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        toast({
          title: "Couldn't save your secure file",
          description: "Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="status"
      data-testid="secure-file-banner"
      className="flex items-center justify-between gap-3 px-4 py-2.5 border-b"
      style={{ backgroundColor: "#FBF1D9", borderColor: "#E6D5A8" }}
    >
      <div className="flex items-center gap-2 text-sm" style={{ color: "#8A6D1F" }}>
        <ShieldAlert className="h-4 w-4 shrink-0" />
        <span>
          You have <span className="font-semibold">{count}</span> unsaved sensitive{" "}
          {count === 1 ? "field" : "fields"}.
        </span>
      </div>
      <button
        type="button"
        onClick={handleSave}
        disabled={busy}
        data-testid="secure-file-banner-save"
        className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        style={{ backgroundColor: "#8A6D1F" }}
      >
        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        Save secure file
      </button>
    </div>
  );
}
